import {
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  NotificationsActive,
  NotificationsOff,
} from '@mui/icons-material';
import { SwiftFlowError } from '@/services/api';

function truncate(text: string, maxLength: number) {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}…`;
}

interface SwiftFlowErrorsTableProps {
    errors: SwiftFlowError[];
    totalCount: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (event: unknown, page: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onEdit: (error: SwiftFlowError) => void;
    onDelete: (error: SwiftFlowError) => void | Promise<void>;
    isDeleting: boolean;
    canDelete: boolean;
}

export function SwiftFlowErrorsTable({
    errors,
    totalCount,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    onEdit,
    onDelete,
    isDeleting,
    canDelete,
}: SwiftFlowErrorsTableProps) {
    const paginated = errors;

    return (
        <Paper>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Gateway</TableCell>
                            <TableCell>Error Code</TableCell>
                            <TableCell>Gateway Error Message</TableCell>
                            <TableCell>SwiftFlow Message</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Notify</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginated.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                    No Swift Flow errors found
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginated.map((error) => (
                                <TableRow key={error.id} hover>
                                    <TableCell>
                                        {error.gateway ? (
                                            <Chip label={error.gateway} size="small" />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                -
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{error.swiftFlowErrorCode}</TableCell>
                                    <TableCell>
                                        <Tooltip title={error.originalErrorMessage} arrow>
                                            <Typography variant="body2">
                                                {truncate(error.originalErrorMessage, 80)}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={error.swiftFlowUnifiedMessage} arrow>
                                            <Typography variant="body2">
                                                {truncate(error.swiftFlowUnifiedMessage || '', 80)}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>{error.errorType || 'None'}</TableCell>
                                    <TableCell>
                                        {error.notify ? (
                                            <NotificationsActive fontSize="small" color="primary" />
                                        ) : (
                                            <NotificationsOff fontSize="small" color="disabled" />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => onEdit(error)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        {canDelete && (
                                          <IconButton
                                            size="small"
                                            color="error"
                                            disabled={isDeleting}
                                            onClick={() => onDelete(error)}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50]}
            />
        </Paper>
    );
}
